import { gql } from "@apollo/client";

export const FETCH_NEW_PRODUCTS = gql`
  query FETCH_NEW_PRODUCTS {
    products(isNew: true, productType: "normal") {
      results {
        id
        company
        name
        number
        version
        odooId
        isActive
        createdAt
      }
    }
  }
`;

export const FETCH_WIS_PRODUCTS = gql`
  query FETCH_WIS_PRODUCTS(
    $offset: Int
    $limit: Int
    $isActiveOnly: Boolean
    $q: String
    $company: String
  ) {
    products(
      productType: "normal"
      offset: $offset
      limit: $limit
      isActiveOnly: $isActiveOnly
      q: $q
      company: $company
    ) {
      total
      results {
        id
        company
        name
        number
        odooId
        isActive
        createdAt
        shape
        images {
          id
          url(width: 300)
        }
        version
      }
    }
  }
`;

export const FRAGMENT_PRODUCT_SIMPLE_FIELDS = gql`
  fragment productSimpleFields on ProductType {
    odooTemplateId
    odooId
    odooIdInc
    odooCategoryId
    productType
    state
    name
    nameCn
    company
    packingName
    number
    version
    countryCodeOfOrigin
    description
    defaultQty
    itemsPerSet
    shape
    ballDiameter
    cubeX
    cubeZ
    cubeY
    cylindricalDiameter
    cylindricalHeight
    flatX
    flatY
    multiSizes
    packing
    shippingMarkName
    colors
    productMaterial
    useProductLineImagesIfNoImages
    innerBoxBarcodeEan
    innerBoxBarcodeUpc
    outerCartonBarcodeEan
    outerCartonBarcodeUpc
    innerCartonBarcodeEan
    innerCartonBarcodeUpc
    innerBoxBarcodeJd
    outerCartonBarcodeJd
    cccInfo
    itemWeight
    grossWeight
    quantityPerDisplayBox
    quantityPerCarton
    innerBoxX
    innerBoxZ
    innerBoxY
    displayBoxX
    displayBoxZ
    displayBoxY
    innerCartonX
    innerCartonZ
    innerCartonY
    outerCartonX
    outerCartonZ
    outerCartonY
    ctnNetWeight
    ctnGrossWeight
    outerCartonCbm
    archivedAt
    isCoreProduct
    isActive
    isActiveForBiz
    isLaborCost
    isSalable
    isPurchasable
    endProductionAt
    careProduction
    moq
    reminderForMissingBeautyShot
    note
  }
`;

export const FRAGMENT_PRODUCT_DETAIL = gql`
  fragment productDetail on ProductType {
    id
    ...productSimpleFields
    images {
      id
      url(width: 300)
    }
    defaultSupplier {
      id
      name
    }
    producer {
      id
      name
    }
    deliverToSupplier {
      id
      name
    }
    costSuppliers {
      id
      supplier {
        id
        nickName
      }
      company
    }
    productLine {
      id
    }
    materials {
      id
      material {
        id
        name
      }
      percentage
      remark
    }
    productList {
      id
    }
    gtmProject {
      id
      name
      state
    }
    activePrices {
      id
      company
      factory {
        id
        odooId
        name
      }
      minQty
      currency
      price
      supplierUpdatedAt
      avaliableFromMonth
      avaliableToMonth
      archivedAt
      archivedCode
      archivedNotes
    }
    children {
      id
      product {
        id
        name
        company
        materials {
          id
          material {
            id
            name
          }
        }
        number
        odooId
        prices {
          id
          factory {
            id
            odooId
            name
          }
          minQty
          currency
          price
          avaliableFromMonth
          avaliableToMonth
          supplierUpdatedAt
          archivedAt
          archivedCode
          archivedNotes
        }
      }
      qty
    }
    considerBomInProduction
    production {
      id
    }
    productionCategory {
      id
    }
    warnings {
      id
    }
    labTestStandards {
      id
    }
    pantone

    complianceCertificates {
      id
      certificateType
      certificateNumber
      title
      fileUrls
    }
    labTestReports {
      id
      title
      reportNumber
      fileUrls
      standards {
        id
        name
      }
    }
  }
  ${FRAGMENT_PRODUCT_SIMPLE_FIELDS}
`;

export const FRAGMENT_PRODUCT_LINE_DETAIL = gql`
  fragment productLine on ProductLineType {
    id
    name
    number
    nameForOdooTemplate
    hsCode
    hsCodeForEu
    hsCodeForUs
    age
    description
    pantone
    printingMethod
    materials {
      id
      material {
        id
        name
      }
      percentage
      remark
    }
    warnings {
      id
      name
    }
    labTestStandards {
      id
      name
    }
    children {
      id
      name
    }
    customsName
    customsBrand
    customsUsage
    customsValue
    customsSize
    customsMaterials

    activeForWis
    fromProductList {
      id
    }
  }
`;

export const SAVE_PRODUCT = gql`
  mutation SAVE_PRODUCT(
    $id: ID
    $simpleFields: ProductSimpleFields
    $activePrices: [ProductPriceInputType]
    $children: [InputProductChildType]
    $costSuppliers: [ProductSupplierInputType]
    $certificateIds: [ID]
    $testReportIds: [ID]
  ) {
    saveProduct(
      id: $id
      simpleFields: $simpleFields
      activePrices: $activePrices
      costSuppliers: $costSuppliers
      children: $children
      certificateIds: $certificateIds
      testReportIds: $testReportIds
    ) {
      product {
        ...productDetail
      }
    }
  }
  ${FRAGMENT_PRODUCT_DETAIL}
`;

export const FETCH_COMPLIANCE_CERTIFICATES_FOR_SELECTOR = gql`
  query FETCH_COMPLIANCE_CERTIFICATES_FOR_SELECTOR($q: String) {
    certificates(q: $q) {
      results {
        id
        certificateType
        title
        certificateNumber
      }
    }
  }
`;

export const FETCH_PRODUCT_DETAIL = gql`
  query FETCH_PRODUCT_DETAIL($id: ID!) {
    product(id: $id) {
      ...productDetail
    }
  }
  ${FRAGMENT_PRODUCT_DETAIL}
`;

export const BATCH_UPDATE_PRODUCTS = gql`
  mutation BATCH_UPDATE_PRODUCTS(
    $ids: [ID!]!
    $innerBoxX: Float
    $innerBoxY: Float
    $innerBoxZ: Float
    $displayBoxX: Float
    $displayBoxY: Float
    $displayBoxZ: Float
    $innerCartonX: Float
    $innerCartonY: Float
    $innerCartonZ: Float
    $outerCartonX: Float
    $outerCartonY: Float
    $outerCartonZ: Float
    $ctnNetWeight: Float
    $ctnGrossWeight: Float
    $itemWeight: Float
    $grossWeight: Float
    $shape: String
    $ballDiameter: Float
    $cubeX: Float
    $cubeY: Float
    $cubeZ: Float
    $cylindricalDiameter: Float
    $cylindricalHeight: Float
    $flatX: Float
    $flatY: Float
    $multiSizes: String
  ) {
    batchUpdateProducts(
      ids: $ids
      innerBoxX: $innerBoxX
      innerBoxY: $innerBoxY
      innerBoxZ: $innerBoxZ
      displayBoxX: $displayBoxX
      displayBoxY: $displayBoxY
      displayBoxZ: $displayBoxZ
      innerCartonX: $innerCartonX
      innerCartonY: $innerCartonY
      innerCartonZ: $innerCartonZ
      outerCartonX: $outerCartonX
      outerCartonY: $outerCartonY
      outerCartonZ: $outerCartonZ
      ctnNetWeight: $ctnNetWeight
      ctnGrossWeight: $ctnGrossWeight
      itemWeight: $itemWeight
      grossWeight: $grossWeight
      shape: $shape
      ballDiameter: $ballDiameter
      cubeX: $cubeX
      cubeY: $cubeY
      cubeZ: $cubeZ
      cylindricalDiameter: $cylindricalDiameter
      cylindricalHeight: $cylindricalHeight
      flatX: $flatX
      flatY: $flatY
      multiSizes: $multiSizes
    ) {
      products {
        id
        innerBoxX
        innerBoxY
        innerBoxZ
        displayBoxX
        displayBoxY
        displayBoxZ
        innerCartonX
        innerCartonY
        innerCartonZ
        outerCartonX
        outerCartonY
        outerCartonZ
        outerCartonCbm
        ctnNetWeight
        ctnGrossWeight
        itemWeight
        grossWeight
        shape
        ballDiameter
        cubeX
        cubeY
        cubeZ
        cylindricalDiameter
        cylindricalHeight
        flatX
        flatY
        multiSizes
      }
    }
  }
`;

export const SYNC_PRODUCT_PRICES_TO_ODOO = gql`
  mutation SYNC_PRODUCT_PRICES_TO_ODOO($id: ID!, $computed: Boolean) {
    syncProductPricesToOdoo(id: $id, computed: $computed) {
      product {
        id
      }
    }
  }
`;

export const FETCH_ODOO_PRODUCTS_FOR_VALIDATION = gql`
  query FETCH_ODOO_PRODUCTS_FOR_VALIDATION(
    $instance: String!
    $ids: [Int]
    $fields: [String]
  ) {
    odooProducts(instance: $instance, ids: $ids, fields: $fields)
  }
`;
